/**
 * The page tab strip, drawn exactly as `PageTabs` draws it, with the caller
 * deciding which tab is lit.
 *
 * `PageTabs` lights a tab when `NavLink` matches its path, which is right for
 * a strip of plain routes and wrong for two cases these screens have. Settings
 * lights Integrations on `/settings/engine`, which is reached from a card on
 * that tab. Approvals keeps its views in `?view=`, the way Salesforge keeps its
 * settings tabs in `?tab=`, and a path match lights every tab of a query strip
 * at once.
 *
 * The classes are `PageTabs`' own, copied rather than imported because that
 * component does not export its face. If `PageTabs` grows an `active` override
 * this file goes and its callers pass that instead.
 *
 * Settings holds more tabs than a narrower window can show, and the strip hides
 * its scrollbar, so it is drawn through `ScrollableStrip` as `PageTabs` is: an
 * arrow at an end with tabs hidden beyond it, the strip scrolling while the
 * mouse rests by that edge, the wheel turned sideways and the lit tab brought
 * into view (the owner, 2026-11-02). Nothing extra shows while the tabs fit.
 */
import type { ReactNode } from 'react'
import { Link, type To } from 'react-router-dom'
import { IconAlertCircle, IconHelpCircle, Tooltip } from '../../components'
import { ScrollableStrip } from '../../components/ScrollableStrip'
import { cn } from '../../lib/cn'

export interface StripTab {
  /** Stable key, and what `active` names. */
  key: string
  to: To
  label: ReactNode
  /** A count chip after the label, as Salesforge puts on a few of its tabs. */
  count?: number
  /** The `(?)` after the label, with this sentence behind it. */
  help?: string
  /**
   * Something behind this tab needs a person, said on the tab itself, the dot
   * `TabNav` draws. A closed tab can otherwise hide the one warning on its page.
   */
  attention?: boolean
  /** Replace the history entry, for a view switch that should not stack up Back presses. */
  replace?: boolean
}

export function PageTabStrip({
  tabs,
  active,
  label,
  className,
}: {
  tabs: readonly StripTab[]
  /** The key of the lit tab, or null for none. */
  active: string | null
  /** The strip's accessible name. */
  label: string
  className?: string
}) {
  return (
    <ScrollableStrip
      as="nav"
      aria-label={label}
      className={cn(
        'flex items-end gap-4 overflow-x-auto border-b border-line px-4 [scrollbar-width:none] lg:px-8 [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === active
        return (
          <Link
            key={tab.key}
            to={tab.to}
            replace={tab.replace}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'relative -mb-px flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-t-md border-b-2 px-1 pb-[11px] text-base font-semibold transition-colors',
              'outline-none focus-visible:ring-4 focus-visible:ring-primary-100',
              isActive
                ? 'border-primary-500 text-accent-text'
                : 'border-transparent text-ink-subtle hover:text-ink',
            )}
          >
            {tab.label}
            {tab.help === undefined ? null : (
              <Tooltip content={tab.help}>
                <IconHelpCircle aria-hidden="true" className="h-4 w-4 text-ink-faint" />
              </Tooltip>
            )}
            {tab.attention ? (
              <span
                role="img"
                aria-label="needs attention"
                className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border border-danger-soft-border bg-danger-soft text-danger"
              >
                <IconAlertCircle aria-hidden="true" className="h-3.5 w-3.5" />
              </span>
            ) : null}
            {tab.count === undefined ? null : (
              <span
                className={cn(
                  'inline-flex h-[22px] items-center rounded-full border px-2 text-xs font-medium tabular-nums',
                  isActive
                    ? 'border-accent-soft-border bg-accent-soft text-accent-text'
                    : 'border-neutral-soft-border bg-neutral-soft text-neutral-text',
                )}
              >
                {tab.count}
              </span>
            )}
          </Link>
        )
      })}
    </ScrollableStrip>
  )
}
